import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import { toast } from "sonner";

const INTENSITY_METRICS = [
  { value: "flavor", label: "Flavor" },
  { value: "color", label: "Color" },
  { value: "aroma", label: "Aroma" },
  { value: "heat", label: "Heat" },
  { value: "pungency", label: "Pungency" },
  { value: "sweetness", label: "Sweetness" },
  { value: "earthiness", label: "Earthiness" },
];

const INTENSITY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const SPICE_CATEGORIES = ["powder", "whole", "blend", "masala", "herbs", "seasoning"];

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingVariantId, setSavingVariantId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [p, v] = await Promise.all([
        api.get(`/admin/products/${id}`),
        api.get(`/admin/variants`, { params: { product_id: id } }),
      ]);
      setProduct({
        ...p.data,
        category: p.data?.category || "",
        intensity_metric: p.data?.intensity_metric || "flavor",
        intensity_level: p.data?.intensity_level || "medium",
        intensity_notes: p.data?.intensity_notes || "",
        benefits: Array.isArray(p.data?.benefits) ? p.data.benefits : [],
        image_urls: Array.isArray(p.data?.image_urls) ? p.data.image_urls : [],
      });
      setVariants(Array.isArray(v.data) ? v.data : []);
    } catch {
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const saveProduct = async () => {
    setSavingProduct(true);
    try {
      await api.patch(`/admin/products/${id}`, {
        name: product.name,
        category: product.category,
        description: product.description,
        intensity_metric: product.intensity_metric,
        intensity_level: product.intensity_level,
        intensity_notes: product.intensity_notes,
        benefits: product.benefits,
        image_urls: product.image_urls,
        is_active: product.is_active,
      });
      toast.success("Product updated");
    } catch {
      toast.error("Failed to save product");
    } finally {
      setSavingProduct(false);
    }
  };

  const toggleProduct = async () => {
    try {
      const res = await api.put(`/admin/products/${id}/toggle`);
      setProduct({ ...product, is_active: res.data.is_active });
      toast.success(res.data.is_active ? "Product enabled" : "Product disabled");
    } catch {
      toast.error("Failed to update product status");
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: "POST", body: formData }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || "Upload failed");

      setProduct((prev) => ({
        ...prev,
        image_urls: [data.secure_url],
      }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateVariant = (variantId, field, value) => {
    setVariants((prev) =>
      prev.map((item) => (item.id === variantId ? { ...item, [field]: value } : item))
    );
  };

  const saveVariant = async (variant) => {
    setSavingVariantId(variant.id);
    try {
      await api.put(`/admin/variants/${variant.id}`, {
        ...variant,
        mrp: Number(variant.mrp),
        discount_percent: Number(variant.discount_percent || 0),
      });
      toast.success(`${variant.size} variant updated`);
    } catch {
      toast.error("Failed to save variant");
    } finally {
      setSavingVariantId(null);
    }
  };

  const toggleStock = async (variant) => {
    try {
      const res = await api.put(`/admin/products/variants/${variant.id}/stock`);
      updateVariant(variant.id, "in_stock", res.data.in_stock);
      toast.success(res.data.in_stock ? "Variant marked in stock" : "Variant marked out of stock");
    } catch {
      toast.error("Failed to update stock status");
    }
  };

  const forceDeleteProduct = async () => {
    const confirmed = window.confirm(
      "This will permanently delete the product and all variants. Continue?"
    );
    if (!confirmed) return;

    try {
      const res = await api.delete(`/admin/products/${id}/force`);
      if (res.status === 200 && res.data?.success) {
        toast.success("Product permanently deleted");
        navigate("/admin/products");
      } else {
        toast.error("Unexpected response from server");
      }
    } catch {
      toast.error("Failed to delete product");
    }
  };

  if (loading || !product) {
    return (
      <div className="bg-white border rounded-2xl p-8 text-gray-600">
        Loading product details...
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <button
              onClick={() => navigate("/admin/products")}
              className="text-sm text-amber-700 hover:text-amber-800"
            >
              ← Back to Products
            </button>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">{product.name}</h1>
            <p className="text-sm text-gray-600 mt-1">
              Edit product details, spice profile, media and variant pricing.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={toggleProduct}
              className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                product.is_active ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {product.is_active ? "Disable Product" : "Enable Product"}
            </button>
            <button
              onClick={saveProduct}
              disabled={savingProduct}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
            >
              {savingProduct ? "Saving..." : "Save Product"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Product Information</h2>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input
              className="input w-full"
              value={product.name}
              onChange={(e) => setProduct({ ...product, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input
              className="input w-full"
              list="spice-category-options"
              placeholder="Category (e.g. powder, blend)"
              value={product.category}
              onChange={(e) => setProduct({ ...product, category: e.target.value })}
            />
            <datalist id="spice-category-options">
              {SPICE_CATEGORIES.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intensity Metric</label>
            <select
              className="input w-full"
              value={product.intensity_metric}
              onChange={(e) => setProduct({ ...product, intensity_metric: e.target.value })}
            >
              {INTENSITY_METRICS.map((metric) => (
                <option key={metric.value} value={metric.value}>
                  {metric.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intensity Level</label>
            <select
              className="input w-full"
              value={product.intensity_level}
              onChange={(e) => setProduct({ ...product, intensity_level: e.target.value })}
            >
              {INTENSITY_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            className="input w-full min-h-[120px]"
            value={product.description}
            onChange={(e) => setProduct({ ...product, description: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Intensity Notes (optional)</label>
          <textarea
            className="input w-full min-h-[90px]"
            placeholder="Example: Deep yellow shade and high visual richness in gravies."
            value={product.intensity_notes || ""}
            onChange={(e) => setProduct({ ...product, intensity_notes: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
          <input
            className="input w-full"
            placeholder="Comma separated benefits"
            value={(product.benefits || []).join(", ")}
            onChange={(e) =>
              setProduct({
                ...product,
                benefits: e.target.value
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Product Image</h2>
        <div className="flex flex-wrap gap-3">
          {product.image_urls.map((img, i) => (
            <img key={i} src={img} alt="" className="h-24 w-24 object-cover rounded-lg border border-gray-200" />
          ))}
        </div>
        <input type="file" accept="image/*" onChange={handleImageChange} />
        {uploading && <p className="text-sm text-gray-600">Uploading image...</p>}
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Variants</h2>
          <p className="text-sm text-gray-500">{variants.length} total variant(s)</p>
        </div>

        <div className="space-y-4">
          {variants.map((v) => {
            const mrp = Number(v.mrp || 0);
            const discount = Number(v.discount_percent || 0);
            const selling = mrp - (mrp * discount) / 100;

            return (
              <div key={v.id} className="rounded-xl border border-gray-200 p-4 bg-gray-50/70">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{v.size}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleStock(v)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                        v.in_stock
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}
                    >
                      {v.in_stock ? "In Stock" : "Out of Stock"}
                    </button>
                    <label className="text-xs text-gray-600 flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={v.is_active}
                        onChange={() => updateVariant(v.id, "is_active", !v.is_active)}
                      />
                      Enabled
                    </label>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">MRP</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={v.mrp}
                      onChange={(e) => updateVariant(v.id, "mrp", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Discount %</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={v.discount_percent || ""}
                      onChange={(e) => updateVariant(v.id, "discount_percent", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Selling Price</label>
                    <input type="text" className="input w-full bg-white" disabled value={selling.toFixed(2)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">SKU</label>
                    <input type="text" className="input w-full bg-white" disabled value={v.sku || ""} />
                  </div>
                </div>

                <div className="flex justify-end mt-3">
                  <button
                    onClick={() => saveVariant(v)}
                    disabled={savingVariantId === v.id}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                  >
                    {savingVariantId === v.id ? "Saving..." : "Save Variant"}
                  </button>
                </div>
              </div>
            );
          })}

          {variants.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              No variants found for this product.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-red-200 p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-red-700">Danger Zone</h2>
        <p className="text-sm text-gray-600 mt-1">
          Permanently delete this product and all linked variants. This action cannot be undone.
        </p>
        <button
          onClick={forceDeleteProduct}
          className="mt-3 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Force Delete Product
        </button>
      </div>
    </div>
  );
};

export default EditProduct;
